import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clipboard,
  Clock3,
  ExternalLink,
  RefreshCw,
  Server,
  Wrench,
} from 'lucide-react';
import { Card } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { toast } from 'sonner';

const STATUS_TONE = {
  up: {
    badge: 'border-emerald-200 bg-emerald-50/80 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300',
    card: 'border-slate-200/80 bg-slate-50/70 dark:border-white/5 dark:bg-white/[0.02]',
    icon: CheckCircle2,
    label: 'Up',
  },
  degraded: {
    badge: 'border-amber-300/80 bg-amber-50 text-amber-900 dark:border-amber-400/30 dark:bg-amber-400/15 dark:text-amber-200',
    card: 'border-amber-300/80 bg-[linear-gradient(135deg,rgba(255,251,235,0.98),rgba(255,247,237,0.96))] shadow-[0_12px_30px_-24px_rgba(180,83,9,0.45),0_0_0_1px_rgba(245,158,11,0.16)] dark:border-amber-400/45 dark:bg-[#2b1d08]',
    icon: AlertTriangle,
    label: 'Degraded',
  },
  down: {
    badge: 'border-red-300 bg-red-100 text-red-800 dark:border-red-400/30 dark:bg-red-400/15 dark:text-red-200',
    card: 'border-red-300 bg-red-50 shadow-[0_0_0_1px_rgba(248,113,113,0.2)] dark:border-red-400/35 dark:bg-red-500/12',
    icon: AlertTriangle,
    label: 'Down',
  },
  unknown: {
    badge: 'border-slate-200 bg-slate-100 text-slate-700 dark:border-slate-400/20 dark:bg-slate-400/10 dark:text-slate-300',
    card: 'border-slate-200 bg-white dark:border-white/10 dark:bg-white/[0.03]',
    icon: Server,
    label: 'Unknown',
  },
};

const STATUS_PRIORITY = {
  down: 3,
  degraded: 2,
  up: 1,
  unknown: 0,
};

const HISTORY_OPTIONS = [6, 24, 72, 168];
const CANONICAL_SERVICE_KEYS = new Set([
  'production-public-frontend',
  'production-admin-frontend',
  'production-backend-api',
  'production-backend-email-drafting-config',
  'local-public-frontend',
  'local-admin-frontend',
  'local-backend-api',
  'local-backend-email-drafting-config',
]);
const LOCAL_BACKEND_START_COMMAND = 'cd /Users/kevindarby/Documents/GitHub/dad-partners && ./scripts/run_local_backend_with_railway_env.sh';
const LOCAL_PUBLIC_FRONTEND_START_COMMAND = 'cd /Users/kevindarby/Documents/GitHub/dad-partners/frontend && yarn dev';
const LOCAL_ADMIN_FRONTEND_START_COMMAND = 'cd /Users/kevindarby/Documents/GitHub/dad-partners/frontend-admin && yarn dev';
const INCIDENT_FILTERS = ['all', 'production', 'local'];
const LOCAL_STALE_THRESHOLD_MINUTES = 3;
const STALE_WARNING_CARD_CLASS = 'border-amber-300/80 bg-[linear-gradient(135deg,rgba(255,251,235,0.98),rgba(255,247,237,0.96))] shadow-[0_12px_30px_-24px_rgba(180,83,9,0.45),0_0_0_1px_rgba(245,158,11,0.16)] dark:border-amber-400/45 dark:bg-[#2b1d08]';
const LOCAL_SERVICE_GUIDE = [
  {
    serviceKey: 'local-public-frontend',
    label: 'Local Public Frontend',
    port: '3000',
    targetUrl: 'http://127.0.0.1:3000/',
    purpose: 'Public Dad Partners site on this laptop.',
    startCommand: LOCAL_PUBLIC_FRONTEND_START_COMMAND,
  },
  {
    serviceKey: 'local-admin-frontend',
    label: 'Local Admin Frontend',
    port: '3001',
    targetUrl: 'http://127.0.0.1:3001/admin',
    purpose: 'Admin app on this laptop.',
    startCommand: LOCAL_ADMIN_FRONTEND_START_COMMAND,
  },
  {
    serviceKey: 'local-backend-api',
    label: 'Local Backend API',
    port: '8000',
    targetUrl: 'http://127.0.0.1:8000/api/health',
    purpose: 'FastAPI backend used by both local frontends.',
    startCommand: LOCAL_BACKEND_START_COMMAND,
  },
];

function formatTimestamp(value) {
  if (!value) return 'Never';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Never';
  return parsed.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
}

function formatResponseTime(value) {
  if (value === null || value === undefined) return 'n/a';
  if (value === 0) return 'config check';
  return `${value} ms`;
}

function isHttpTargetUrl(value) {
  return /^https?:\/\//i.test(String(value || '').trim());
}

function formatSourceTypeLabel(value) {
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) return 'Unknown source';
  if (normalized === 'local-watcher') return 'Watcher';
  if (normalized === 'admin-manual-check') return 'Manual admin check';
  if (normalized === 'uptimerobot') return 'UptimeRobot';
  return normalized
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function formatSourceMachineLabel(value) {
  const raw = String(value || '').trim();
  if (!raw) return 'Unknown machine';
  if (raw.startsWith('admin-ui:')) return 'Admin UI';
  if (raw.toLowerCase() === 'uptimerobot') return 'UptimeRobot';
  return raw;
}

function buildSourceDisplay(source) {
  const machineLabel = formatSourceMachineLabel(source?.source_machine);
  const typeLabel = formatSourceTypeLabel(source?.source_type);
  const rawMachine = String(source?.source_machine || '').trim();
  const detail = rawMachine && rawMachine !== machineLabel ? rawMachine : '';

  return {
    machineLabel,
    typeLabel,
    detail,
  };
}

function buildEnvironmentSources(services = []) {
  const map = new Map();

  services.forEach((service) => {
    (service.sources || []).forEach((source) => {
      const display = buildSourceDisplay(source);
      const key = `${source.source_machine || 'unknown'}::${source.source_type || 'unknown'}`;
      const previous = map.get(key);
      const sourceTime = new Date(source.checked_at || 0).getTime();
      const previousTime = new Date(previous?.checked_at || 0).getTime();

      if (!previous || sourceTime > previousTime) {
        map.set(key, {
          key,
          checked_at: source.checked_at,
          source_machine: source.source_machine,
          source_type: source.source_type,
          ...display,
        });
      }
    });
  });

  return Array.from(map.values()).sort((a, b) => {
    const aTime = new Date(a.checked_at || 0).getTime();
    const bTime = new Date(b.checked_at || 0).getTime();
    return bTime - aTime;
  });
}

function buildGroupedSourceSummaries(sources = []) {
  const map = new Map();

  sources.forEach((source) => {
    const display = buildSourceDisplay(source);
    const key = `${display.machineLabel}::${display.typeLabel}`;
    const previous = map.get(key);
    const sourceTime = new Date(source.checked_at || 0).getTime();
    const previousTime = new Date(previous?.checked_at || 0).getTime();

    if (!previous) {
      map.set(key, {
        key,
        machineLabel: display.machineLabel,
        typeLabel: display.typeLabel,
        count: 1,
        checked_at: source.checked_at,
      });
      return;
    }

    previous.count += 1;
    if (sourceTime > previousTime) {
      previous.checked_at = source.checked_at;
    }
    map.set(key, previous);
  });

  return Array.from(map.values()).sort((a, b) => {
    const aTime = new Date(a.checked_at || 0).getTime();
    const bTime = new Date(b.checked_at || 0).getTime();
    return bTime - aTime;
  });
}

function isCanonicalMonitoringRecord(item) {
  const serviceKey = String(item?.service_key || '').trim().toLowerCase();
  return CANONICAL_SERVICE_KEYS.has(serviceKey);
}

function buildAttentionState(productionSummary, localSummary) {
  if (productionSummary.downCount > 0) {
    return {
      status: 'down',
      title: 'Production needs attention now',
      detail: `${productionSummary.downCount} production service${productionSummary.downCount === 1 ? '' : 's'} are down. Start in the production column and verify whether another source agrees.`,
    };
  }

  if (productionSummary.degradedCount > 0) {
    return {
      status: 'degraded',
      title: 'Production looks shaky',
      detail: `${productionSummary.degradedCount} production service${productionSummary.degradedCount === 1 ? '' : 's'} are degraded. Check response times and recent incidents before this turns into a full outage.`,
    };
  }

  if (localSummary.downCount > 0) {
    return {
      status: 'degraded',
      title: 'A local machine needs attention',
      detail: `${localSummary.downCount} local service${localSummary.downCount === 1 ? '' : 's'} are down. This is probably machine-specific unless another watcher reports the same thing.`,
    };
  }

  if (localSummary.degradedCount > 0) {
    return {
      status: 'degraded',
      title: 'Local checks need a look',
      detail: `${localSummary.degradedCount} local service${localSummary.degradedCount === 1 ? '' : 's'} are degraded. Investigate on the affected machine if that matters right now.`,
    };
  }

  if (localSummary.isStale) {
    const roundedMinutes = Math.max(1, Math.round(localSummary.staleMinutes || 0));
    return {
      status: 'degraded',
      title: 'Local watcher data is stale',
      detail: `The last local watcher report is ${roundedMinutes} minute${roundedMinutes === 1 ? '' : 's'} old, so this page cannot honestly confirm current local status until that machine reports again.`,
    };
  }

  return {
    status: 'up',
    title: 'Nothing needs your move right now',
    detail: 'Production and local checks are currently healthy, so the page can stay quiet until something actually needs attention.',
  };
}

function buildAttentionItems(services = []) {
  return services
    .filter((service) => service.aggregated_status === 'down' || service.aggregated_status === 'degraded')
    .sort((a, b) => {
      const statusCompare = (STATUS_PRIORITY[b.aggregated_status] ?? 0) - (STATUS_PRIORITY[a.aggregated_status] ?? 0);
      if (statusCompare !== 0) return statusCompare;
      if (a.environment !== b.environment) {
        return a.environment === 'production' ? -1 : 1;
      }
      return a.service_name.localeCompare(b.service_name);
    })
    .map((service) => ({
      key: `${service.service_key}-${service.environment}`,
      serviceName: service.service_name,
      environment: service.environment,
      status: service.aggregated_status,
      checkedAt: service.last_checked_at,
      sourceCount: (service.sources || []).length,
    }));
}

function splitServicesByAttention(services = []) {
  return {
    active: services.filter((service) => service.aggregated_status === 'down' || service.aggregated_status === 'degraded'),
    healthy: services.filter((service) => service.aggregated_status === 'up' || service.aggregated_status === 'unknown'),
  };
}

function buildEnvironmentSummary(services = [], sources = []) {
  const counts = { up: 0, degraded: 0, down: 0 };
  let lastCheckedAt = null;

  services.forEach((service) => {
    if (service.aggregated_status === 'up') counts.up += 1;
    else if (service.aggregated_status === 'degraded') counts.degraded += 1;
    else if (service.aggregated_status === 'down') counts.down += 1;

    const currentTime = new Date(service.last_checked_at || 0).getTime();
    const bestTime = new Date(lastCheckedAt || 0).getTime();
    if (currentTime > bestTime) {
      lastCheckedAt = service.last_checked_at;
    }
  });

  return {
    serviceCount: services.length,
    sourceCount: sources.length,
    lastCheckedAt,
    downCount: counts.down,
    degradedCount: counts.degraded,
    upCount: counts.up,
  };
}

function getMinutesSince(value) {
  if (!value) return null;
  const parsed = new Date(value);
  const timestamp = parsed.getTime();
  if (Number.isNaN(timestamp)) return null;
  return Math.max(0, (Date.now() - timestamp) / 60000);
}

function withFreshness(summary, thresholdMinutes = null) {
  const staleMinutes = getMinutesSince(summary.lastCheckedAt);
  const isStale = Boolean(
    thresholdMinutes
    && summary.serviceCount > 0
    && staleMinutes !== null
    && staleMinutes > thresholdMinutes
  );

  return {
    ...summary,
    isStale,
    staleMinutes,
  };
}

function formatStaleDetail(summary, scopeLabel) {
  if (!summary.isStale) return '';
  const roundedMinutes = Math.max(1, Math.round(summary.staleMinutes || 0));
  return `Latest ${scopeLabel} report is ${roundedMinutes} minute${roundedMinutes === 1 ? '' : 's'} old, so treat this as stale until the watcher reports again.`;
}

function formatIncidentEventLabel(value) {
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) return 'Update';
  if (normalized === 'repeat_alert') return 'Repeat alert';
  if (normalized === 'recovered') return 'Recovered';
  if (normalized === 'degraded') return 'Degraded';
  if (normalized === 'down') return 'Down';
  return normalized
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function formatEscalationLabel(value) {
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) return '';
  if (normalized === 'active_incident') return 'Active incident';
  if (normalized === 'watch_closely') return 'Watch closely';
  if (normalized === 'machine_specific') return 'Machine specific';
  if (normalized === 'resolved') return 'Resolved';
  if (normalized === 'watch') return 'Watch';
  return normalized
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function buildHistorySummaryMap(items = []) {
  const map = new Map();
  items.forEach((item) => {
    map.set(`${item.service_key}::${item.environment}::${item.source_machine}`, item);
  });
  return map;
}

function groupServices(snapshots = [], historySummary = []) {
  const historyMap = buildHistorySummaryMap(historySummary);
  const groups = new Map();

  snapshots.forEach((snapshot) => {
    const groupKey = `${snapshot.service_key}::${snapshot.environment}`;
    const historyKey = `${snapshot.service_key}::${snapshot.environment}::${snapshot.source_machine}`;
    const history = historyMap.get(historyKey) || null;
    const group = groups.get(groupKey) || {
      service_key: snapshot.service_key,
      service_name: snapshot.service_name,
      environment: snapshot.environment,
      target_url: snapshot.target_url,
      aggregated_status: 'unknown',
      last_checked_at: snapshot.checked_at,
      sources: [],
    };

    const snapshotTime = new Date(snapshot.checked_at || 0).getTime();
    const groupTime = new Date(group.last_checked_at || 0).getTime();
    if (snapshotTime > groupTime) {
      group.last_checked_at = snapshot.checked_at;
      group.aggregated_status = snapshot.status || 'unknown';
    } else if (snapshotTime === groupTime) {
      const snapshotPriority = STATUS_PRIORITY[snapshot.status] ?? 0;
      const groupPriority = STATUS_PRIORITY[group.aggregated_status] ?? 0;
      if (snapshotPriority > groupPriority) {
        group.aggregated_status = snapshot.status || 'unknown';
      }
    }

    group.sources.push({
      ...snapshot,
      history,
    });

    groups.set(groupKey, group);
  });

  return Array.from(groups.values()).map((group) => ({
    ...group,
    sources: [...group.sources].sort((a, b) => {
      const aTime = new Date(a.checked_at || 0).getTime();
      const bTime = new Date(b.checked_at || 0).getTime();
      return bTime - aTime;
    }),
  })).sort((a, b) => {
    const envCompare = a.environment.localeCompare(b.environment);
    if (envCompare !== 0) return envCompare;
    const statusCompare = (STATUS_PRIORITY[b.aggregated_status] ?? 0) - (STATUS_PRIORITY[a.aggregated_status] ?? 0);
    if (statusCompare !== 0) return statusCompare;
    return a.service_name.localeCompare(b.service_name);
  });
}

function SummaryCard({ label, value, detail, status = 'unknown' }) {
  const tone = STATUS_TONE[status] || STATUS_TONE.unknown;
  const Icon = tone.icon;
  return (
    <Card className={`border p-4 ${tone.card}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-gray-500">
            {label}
          </p>
          <p className="mt-3 text-3xl font-semibold text-slate-900 dark:text-white">
            {value}
          </p>
          <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-gray-300">
            {detail}
          </p>
        </div>
        <div className="rounded-full bg-white/80 p-3 text-slate-700 shadow-sm dark:bg-black/20 dark:text-gray-200">
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </Card>
  );
}

function EnvironmentSummaryCard({
  eyebrow,
  title,
  value,
  detail,
  status = 'unknown',
  note = '',
}) {
  const tone = STATUS_TONE[status] || STATUS_TONE.unknown;
  const Icon = tone.icon;
  return (
    <Card className={`border p-4 ${tone.card}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-gray-500">
            {eyebrow}
          </p>
          <p className="mt-2 text-base font-semibold text-slate-900 dark:text-white">
            {title}
          </p>
          <p className="mt-3 text-3xl font-semibold text-slate-900 dark:text-white">
            {value}
          </p>
          <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-gray-300">
            {detail}
          </p>
          {note ? (
            <p className="mt-2 text-xs leading-5 text-slate-500 dark:text-gray-400">
              {note}
            </p>
          ) : null}
        </div>
        <div className="rounded-full bg-white/80 p-3 text-slate-700 shadow-sm dark:bg-black/20 dark:text-gray-200">
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </Card>
  );
}

function RecoveryPolicyCard({
  eyebrow,
  title,
  tone = 'unknown',
  badgeLabel,
  detail,
  note = '',
  actionLabel = '',
  onAction,
  actionDisabled = false,
}) {
  const toneStyles = STATUS_TONE[tone] || STATUS_TONE.unknown;
  const Icon = tone === 'up' ? CheckCircle2 : tone === 'degraded' ? Wrench : AlertTriangle;

  return (
    <Card className={`border p-4 ${toneStyles.card}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="max-w-3xl">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-gray-500">
            {eyebrow}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <p className="text-base font-semibold text-slate-900 dark:text-white">
              {title}
            </p>
            {badgeLabel ? <Badge className={toneStyles.badge}>{badgeLabel}</Badge> : null}
          </div>
          <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-gray-300">
            {detail}
          </p>
          {note ? (
            <p className="mt-2 text-xs leading-5 text-slate-500 dark:text-gray-400">
              {note}
            </p>
          ) : null}
        </div>
        <div className="rounded-full bg-white/80 p-3 text-slate-700 shadow-sm dark:bg-black/20 dark:text-gray-200">
          <Icon className="h-5 w-5" />
        </div>
      </div>
      {actionLabel ? (
        <div className="mt-4">
          <Button
            type="button"
            variant="outline"
            onClick={onAction}
            disabled={actionDisabled}
            className="border-slate-200 bg-white text-slate-900 hover:bg-slate-50 disabled:opacity-60 dark:border-white/10 dark:bg-black/20 dark:text-white dark:hover:bg-black/30"
          >
            <Clipboard className="mr-2 h-4 w-4" />
            {actionLabel}
          </Button>
        </div>
      ) : null}
    </Card>
  );
}

function AttentionBanner({ attention }) {
  const tone = STATUS_TONE[attention.status] || STATUS_TONE.unknown;
  const Icon = tone.icon;
  const isDegraded = attention.status === 'degraded';
  const isStaleAttention = attention.title === 'Local watcher data is stale';
  const eyebrowClass = isDegraded
    ? isStaleAttention
      ? 'text-amber-800 dark:text-amber-300'
      : 'text-amber-800 dark:text-amber-300'
    : 'text-slate-500 dark:text-gray-500';
  const titleClass = isDegraded
    ? isStaleAttention
      ? 'text-amber-950 dark:text-amber-50'
      : 'text-amber-950 dark:text-amber-50'
    : 'text-slate-900 dark:text-white';
  const detailClass = isDegraded
    ? isStaleAttention
      ? 'text-amber-900 dark:text-amber-100/90'
      : 'text-amber-900 dark:text-amber-100/90'
    : 'text-slate-700 dark:text-gray-300';
  const iconWrapClass = isDegraded
    ? isStaleAttention
      ? 'bg-amber-100/90 text-amber-800 dark:bg-amber-500/18 dark:text-amber-200'
      : 'bg-amber-100 text-amber-800 dark:bg-amber-500/18 dark:text-amber-200'
    : 'bg-white/90 text-slate-700 dark:bg-black/20 dark:text-gray-200';
  const cardClass = isStaleAttention
    ? STALE_WARNING_CARD_CLASS
    : tone.card;

  return (
    <Card className={`border p-5 ${cardClass}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="max-w-3xl">
          <p className={`text-[11px] font-semibold uppercase tracking-[0.24em] ${eyebrowClass}`}>
            What needs your move right now
          </p>
          <h4 className={`mt-3 text-xl font-semibold ${titleClass}`}>
            {attention.title}
          </h4>
          <p className={`mt-3 text-sm leading-6 ${detailClass}`}>
            {attention.detail}
          </p>
          {attention.items?.length ? (
            <div className="mt-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-gray-500">
                Look here first
              </p>
              <div className="mt-3 grid gap-2 md:grid-cols-2">
                {attention.items.map((item) => {
                  const itemTone = STATUS_TONE[item.status] || STATUS_TONE.unknown;
                  return (
                    <div
                      key={item.key}
                      className={`rounded-xl border px-3 py-3 ${itemTone.card}`}
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-sm font-semibold text-slate-900 dark:text-white">
                          {item.serviceName}
                        </p>
                        <Badge className={itemTone.badge}>{itemTone.label}</Badge>
                        <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-gray-500">
                          {item.environment}
                        </span>
                      </div>
                      <p className="mt-2 text-xs leading-5 text-slate-600 dark:text-gray-300">
                        Last check {formatTimestamp(item.checkedAt)} across {item.sourceCount} reporting source{item.sourceCount === 1 ? '' : 's'}.
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>
        <div className={`rounded-full p-3 shadow-sm ${iconWrapClass}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </Card>
  );
}

function CurrentIssueCard({ item }) {
  const tone = STATUS_TONE[item.status] || STATUS_TONE.unknown;
  return (
    <div className={`rounded-xl border px-4 py-3 ${tone.card}`}>
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-sm font-semibold text-slate-900 dark:text-white">
          {item.serviceName}
        </p>
        <Badge className={tone.badge}>{tone.label}</Badge>
        <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-gray-500">
          {item.environment}
        </span>
      </div>
      <p className="mt-2 text-xs leading-5 text-slate-600 dark:text-gray-300">
        Last check {formatTimestamp(item.checkedAt)} across {item.sourceCount} reporting source{item.sourceCount === 1 ? '' : 's'}.
      </p>
    </div>
  );
}

function LocalQuickActionRow({ item, onCopyStartCommand }) {
  return (
    <div className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-slate-50/80 px-4 py-3 dark:border-white/10 dark:bg-black/10 lg:flex-row lg:items-center lg:justify-between">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm font-semibold text-slate-900 dark:text-white">
            {item.label}
          </p>
          <Badge
            variant="outline"
            className="border-slate-200 bg-white/80 text-slate-700 dark:border-white/10 dark:bg-black/20 dark:text-gray-200"
          >
            Port {item.port}
          </Badge>
        </div>
        <p className="mt-1 text-xs leading-5 text-slate-600 dark:text-gray-300">
          {item.purpose}
        </p>
        <p className="mt-1 break-all text-xs leading-5 text-slate-500 dark:text-gray-400">
          {item.targetUrl}
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={() => onCopyStartCommand(item)}
          className="border-slate-200 bg-white text-slate-900 hover:bg-slate-50 dark:border-white/10 dark:bg-black/20 dark:text-white dark:hover:bg-black/30"
        >
          <Clipboard className="mr-2 h-4 w-4" />
          Copy start command
        </Button>
        <Button
          asChild
          variant="outline"
          className="border-slate-200 bg-white text-slate-900 hover:bg-slate-50 dark:border-white/10 dark:bg-black/20 dark:text-white dark:hover:bg-black/30"
        >
          <a href={item.targetUrl} target="_blank" rel="noreferrer">
            Open target
            <ExternalLink className="ml-2 h-4 w-4" />
          </a>
        </Button>
      </div>
    </div>
  );
}

function SourceStatusRow({ source, historyHours }) {
  const tone = STATUS_TONE[source.status] || STATUS_TONE.unknown;
  const sourceDisplay = buildSourceDisplay(source);
  const rowClass = source.status === 'up'
    ? 'border-slate-200 bg-white/80 dark:border-white/10 dark:bg-black/10'
    : tone.card;
  return (
    <div className={`rounded-xl border px-3 py-3 ${rowClass}`}>
      <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-semibold text-slate-900 dark:text-white">
              {sourceDisplay.machineLabel}
            </p>
            <Badge className={tone.badge}>{tone.label}</Badge>
            <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-gray-500">
              {sourceDisplay.typeLabel}
            </span>
          </div>
          {sourceDisplay.detail ? (
            <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400 dark:text-gray-500">
              {sourceDisplay.detail}
            </p>
          ) : null}
          <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-gray-400">
            Last check {formatTimestamp(source.checked_at)}
          </p>
        </div>
        <div className="text-left text-xs leading-5 text-slate-600 dark:text-gray-300 lg:text-right">
          <p>HTTP {source.http_status || 'n/a'} · {formatResponseTime(source.response_time_ms)}</p>
          {source.history ? (
            <p>
              Last {historyHours}h: {source.history.down_count} down, {source.history.degraded_count} degraded, {source.history.total_checks} checks
            </p>
          ) : null}
        </div>
      </div>
      {source.error_summary ? (
        <p className="mt-2 text-sm leading-6 text-slate-700 dark:text-gray-300">
          {source.error_summary}
        </p>
      ) : null}
    </div>
  );
}

function ServiceCard({ service, historyHours }) {
  const tone = STATUS_TONE[service.aggregated_status] || STATUS_TONE.unknown;
  const Icon = tone.icon;
  const sourceSummary = buildGroupedSourceSummaries(service.sources || []);
  const serviceCardClass = service.aggregated_status === 'up'
    ? 'border-slate-200/80 bg-slate-50/55 dark:border-white/5 dark:bg-white/[0.02]'
    : tone.card;
  const canOpenTarget = isHttpTargetUrl(service.target_url);
  return (
    <Card className={`border p-5 ${serviceCardClass}`}>
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="max-w-3xl">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-lg font-semibold text-slate-900 dark:text-white">
              {service.service_name}
            </p>
            <Badge className={tone.badge}>{tone.label}</Badge>
            <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-gray-500">
              {service.environment}
            </span>
          </div>
          <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-gray-300">
            Latest check seen {formatTimestamp(service.last_checked_at)}
          </p>
          <p className="mt-1 break-all text-xs leading-5 text-slate-500 dark:text-gray-400">
            {service.target_url}
          </p>
          {sourceSummary.length ? (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-gray-500">
                Sources
              </span>
              {sourceSummary.map((source) => (
                <Badge
                  key={source.key}
                  variant="outline"
                  className="border-slate-200 bg-white/80 text-slate-700 dark:border-white/10 dark:bg-black/20 dark:text-gray-200"
                >
                  {source.machineLabel}
                  <span className="ml-1 text-slate-500 dark:text-gray-400">
                    {source.typeLabel.toLowerCase()}
                    {source.count > 1 ? ` x${source.count}` : ''}
                  </span>
                </Badge>
              ))}
            </div>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <div className="rounded-full bg-white/80 p-3 text-slate-700 shadow-sm dark:bg-black/20 dark:text-gray-200">
            <Icon className="h-5 w-5" />
          </div>
          {canOpenTarget ? (
            <Button asChild variant="outline" className="border-slate-200 bg-white text-slate-900 hover:bg-slate-50 dark:border-white/10 dark:bg-black/20 dark:text-white dark:hover:bg-black/30">
              <a href={service.target_url} target="_blank" rel="noreferrer">
                Open target
                <ExternalLink className="ml-2 h-4 w-4" />
              </a>
            </Button>
          ) : null}
        </div>
      </div>

      <div className="mt-4 space-y-3">
        {service.sources.map((source) => (
          <SourceStatusRow
            key={`${source.service_key}-${source.environment}-${source.source_machine}`}
            source={source}
            historyHours={historyHours}
          />
        ))}
      </div>
    </Card>
  );
}

function IncidentRow({ incident, highlighted = false }) {
  const tone = STATUS_TONE[incident.to_status] || STATUS_TONE.unknown;
  const sourceDisplay = buildSourceDisplay(incident);
  const eventLabel = formatIncidentEventLabel(incident.event_type);
  const escalationLabel = formatEscalationLabel(incident.alert_escalation_level);
  const isBackupSource = String(incident.source_type || '').trim().toLowerCase() === 'uptimerobot';
  const rowClass = 'border-slate-200 bg-white/80 dark:border-white/10 dark:bg-black/10';
  return (
    <div className={`rounded-xl border px-4 py-3 ${highlighted ? 'ring-2 ring-aqua/40' : ''} ${rowClass}`}>
      <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-semibold text-slate-900 dark:text-white">
              {incident.service_name}
            </p>
            <Badge className={tone.badge}>{eventLabel}</Badge>
            <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-gray-500">
              {incident.environment}
            </span>
            {isBackupSource ? (
              <Badge
                variant="outline"
                className="border-slate-200 bg-white/80 text-slate-700 dark:border-white/10 dark:bg-black/20 dark:text-gray-200"
              >
                Backup source
              </Badge>
            ) : null}
            {escalationLabel ? (
              <Badge
                variant="outline"
                className="border-slate-200 bg-white/80 text-slate-700 dark:border-white/10 dark:bg-black/20 dark:text-gray-200"
              >
                {escalationLabel}
              </Badge>
            ) : null}
          </div>
          <p className="mt-1 text-sm leading-6 text-slate-600 dark:text-gray-300">
            {incident.message}
          </p>
          {incident.repeat_down_streak ? (
            <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-gray-400">
              Sustained outage streak: {incident.repeat_down_streak} consecutive checks
            </p>
          ) : null}
        </div>
        <div className="text-left text-xs leading-5 text-slate-500 dark:text-gray-400 lg:text-right">
          <p>{sourceDisplay.machineLabel}</p>
          <p>{sourceDisplay.typeLabel}</p>
          <p>{formatTimestamp(incident.observed_at)}</p>
        </div>
      </div>
    </div>
  );
}

export default function MonitoringTab({
  overview,
  loading,
  runningProductionChecks,
  runningLocalChecks,
  historyHours,
  setHistoryHours,
  targetAnchor = null,
  targetRecordId = null,
  targetRequestKey = null,
  onRefresh,
  onRunProductionChecks,
  onRunLocalChecks,
}) {
  const [incidentFilter, setIncidentFilter] = useState('all');
  const [showProductionDetails, setShowProductionDetails] = useState(false);
  const [showLocalSection, setShowLocalSection] = useState(false);
  const [showIncidentHistory, setShowIncidentHistory] = useState(false);
  const [showOperationsGuide, setShowOperationsGuide] = useState(false);
  const [highlightedAnchor, setHighlightedAnchor] = useState(null);
  const productionSectionRef = useRef(null);
  const localSectionRef = useRef(null);
  const operationsGuideRef = useRef(null);
  const incidentHistoryRef = useRef(null);
  const groupedServices = useMemo(
    () => groupServices(
      (overview?.snapshots || []).filter(isCanonicalMonitoringRecord),
      overview?.history_summary || []
    ),
    [overview]
  );

  const summary = useMemo(() => {
    const counts = { up: 0, degraded: 0, down: 0 };
    groupedServices.forEach((service) => {
      if (service.aggregated_status === 'up') counts.up += 1;
      else if (service.aggregated_status === 'degraded') counts.degraded += 1;
      else if (service.aggregated_status === 'down') counts.down += 1;
    });
    return counts;
  }, [groupedServices]);

  const productionServices = groupedServices.filter((item) => item.environment === 'production');
  const localServices = groupedServices.filter((item) => item.environment === 'local');
  const productionSources = useMemo(() => buildEnvironmentSources(productionServices), [productionServices]);
  const localSources = useMemo(() => buildEnvironmentSources(localServices), [localServices]);
  const productionServiceBuckets = useMemo(() => splitServicesByAttention(productionServices), [productionServices]);
  const localServiceBuckets = useMemo(() => splitServicesByAttention(localServices), [localServices]);
  const productionSourceSummary = useMemo(() => buildGroupedSourceSummaries(productionSources), [productionSources]);
  const localSourceSummary = useMemo(() => buildGroupedSourceSummaries(localSources), [localSources]);
  const productionSummary = useMemo(
    () => withFreshness(buildEnvironmentSummary(productionServices, productionSources)),
    [productionServices, productionSources]
  );
  const localSummary = useMemo(
    () => withFreshness(
      buildEnvironmentSummary(localServices, localSources),
      LOCAL_STALE_THRESHOLD_MINUTES
    ),
    [localServices, localSources]
  );
  const attentionState = useMemo(
    () => ({
      ...buildAttentionState(productionSummary, localSummary),
      items: buildAttentionItems(groupedServices),
    }),
    [groupedServices, productionSummary, localSummary]
  );
  const isLocalAdminHost = typeof window !== 'undefined' && (
    window.location.hostname === 'localhost'
    || window.location.hostname === '127.0.0.1'
    || window.location.hostname === '::1'
  );
  const localChecksDisabled = runningLocalChecks || !isLocalAdminHost;
  const localChecksHelperText = isLocalAdminHost
    ? 'Runs only against the local services on this machine.'
    : 'Local checks are only available when this admin app is running on the machine you want to test.';
  const filteredIncidents = useMemo(() => {
    const incidents = (overview?.incidents || []).filter(isCanonicalMonitoringRecord);
    if (incidentFilter === 'production') {
      return incidents.filter((incident) => incident.environment === 'production');
    }
    if (incidentFilter === 'local') {
      return incidents.filter((incident) => incident.environment === 'local');
    }
    return incidents;
  }, [incidentFilter, overview]);
  const productionDetailsOpen = showProductionDetails || productionServiceBuckets.active.length > 0;
  const localSectionOpen = showLocalSection || localServiceBuckets.active.length > 0;
  const copyLocalBackendStartCommand = async () => {
    try {
      await navigator.clipboard.writeText(LOCAL_BACKEND_START_COMMAND);
      toast.success('Local backend start command copied');
    } catch (error) {
      console.error('Failed to copy local backend start command:', error);
      toast.error('Could not copy the local backend start command');
    }
  };
  const copyLocalServiceStartCommand = async (item) => {
    try {
      await navigator.clipboard.writeText(item.startCommand);
      toast.success(`${item.label} start command copied`);
    } catch (error) {
      console.error('Failed to copy local start command:', error);
      toast.error(`Could not copy the ${item.label.toLowerCase()} start command`);
    }
  };

  useEffect(() => {
    if (!targetAnchor || !targetRequestKey) return;

    const normalizedAnchor = String(targetAnchor).trim().toLowerCase();
    const refMap = {
      production: productionSectionRef,
      'production-checks': productionSectionRef,
      local: localSectionRef,
      'local-checks': localSectionRef,
      'operating-guide': operationsGuideRef,
      'incident-history': incidentHistoryRef,
    };
    const targetRef = refMap[normalizedAnchor];
    if (!targetRef?.current) return;

    if (normalizedAnchor === 'local' || normalizedAnchor === 'local-checks') {
      setShowLocalSection(true);
    }
    if (normalizedAnchor === 'production' || normalizedAnchor === 'production-checks') {
      setShowProductionDetails(true);
    }
    if (normalizedAnchor === 'operating-guide') {
      setShowOperationsGuide(true);
    }
    if (normalizedAnchor === 'incident-history') {
      setShowIncidentHistory(true);
    }

    const frame = window.requestAnimationFrame(() => {
      targetRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    });
    setHighlightedAnchor(normalizedAnchor);
    const timeout = window.setTimeout(() => setHighlightedAnchor(null), 1800);
    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timeout);
    };
  }, [targetAnchor, targetRequestKey]);

  const highlightedSectionClass = (anchors) => {
    const values = Array.isArray(anchors) ? anchors : [anchors];
    return values.includes(highlightedAnchor) ? 'ring-2 ring-aqua/40' : '';
  };

  return (
    <div className="space-y-4">
      <Card className="border-slate-200 bg-gradient-to-br from-white via-[#f8fbfd] to-[#eef5fa] p-5 dark:border-white/10 dark:bg-gradient-to-br dark:from-[#20232a] dark:via-[#181c22] dark:to-[#11161b]">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="max-w-3xl">
            <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-sky-700 dark:text-sky-300">
              Monitoring
            </p>
            <h3 className="mt-3 text-2xl font-semibold text-slate-900 dark:text-white">
              Keep production truth separate from machine-specific local truth
            </h3>
            <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-gray-300">
              Production tells you what outside-facing Dad Partners looks like right now. Local tells you what one specific laptop or Mac mini last saw on that machine.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {HISTORY_OPTIONS.map((option) => (
              <Button
                key={option}
                type="button"
                variant={historyHours === option ? 'default' : 'outline'}
                onClick={() => setHistoryHours(option)}
                className={historyHours === option
                  ? 'bg-sky-600 text-white hover:bg-sky-700'
                  : 'border-slate-200 bg-white text-slate-900 hover:bg-slate-50 dark:border-white/10 dark:bg-black/20 dark:text-white dark:hover:bg-black/30'}
              >
                {option < 24 ? `${option}h` : option === 24 ? '24h' : option === 168 ? '7d' : `${Math.round(option / 24)}d`}
              </Button>
            ))}
            <Button
              type="button"
              variant="outline"
              onClick={onRefresh}
              disabled={loading}
              className="border-slate-200 bg-white text-slate-900 hover:bg-slate-50 dark:border-white/10 dark:bg-black/20 dark:text-white dark:hover:bg-black/30"
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh data
            </Button>
          </div>
        </div>
        <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-gray-300">
          {localChecksHelperText}
        </p>
        <p className="mt-2 text-xs leading-5 text-slate-500 dark:text-gray-400">
          Right now: {summary.down} down, {productionSummary.degradedCount + localSummary.degradedCount + (localSummary.isStale ? 1 : 0)} degraded, {productionSummary.upCount + localSummary.upCount} healthy services in view.
        </p>
      </Card>

      <AttentionBanner attention={attentionState} />

      <div className="space-y-4">
        <Card ref={productionSectionRef} className={`border-slate-200 bg-white/90 p-5 dark:border-white/10 dark:bg-white/[0.03] ${highlightedSectionClass(['production', 'production-checks'])}`}>
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-sky-700 dark:text-sky-300" />
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-lg font-semibold text-slate-900 dark:text-white">Production</p>
                  <Badge className="border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-500/20 dark:bg-sky-500/10 dark:text-sky-300">
                    Shared truth
                  </Badge>
                </div>
                <p className="text-sm text-slate-600 dark:text-gray-300">
                  {productionSummary.downCount > 0 || productionSummary.degradedCount > 0
                    ? `${productionSummary.downCount} down, ${productionSummary.degradedCount} degraded, ${productionSummary.upCount} healthy across ${productionSummary.sourceCount} reporting sources.`
                    : `${productionSummary.upCount} healthy services across ${productionSummary.sourceCount} reporting sources.`}
                </p>
              </div>
            </div>
            <Button
              type="button"
              onClick={onRunProductionChecks}
              disabled={runningProductionChecks}
              className="bg-sky-600 text-white hover:bg-sky-700 disabled:opacity-60"
            >
              <Activity className={`mr-2 h-4 w-4 ${runningProductionChecks ? 'animate-pulse' : ''}`} />
              {runningProductionChecks ? 'Running checks...' : 'Run production checks'}
            </Button>
          </div>
          {productionSources.length ? (
            <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50/80 px-4 py-3 dark:border-white/10 dark:bg-black/10">
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-gray-500">
                Reporting sources
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {productionSourceSummary.map((source) => (
                  <div
                    key={source.key}
                    className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-700 dark:border-white/10 dark:bg-black/20 dark:text-gray-200"
                  >
                    <span className="font-semibold">{source.machineLabel}</span>
                    <span className="ml-2 text-slate-500 dark:text-gray-400">
                      {source.typeLabel}
                      {source.count > 1 ? ` x${source.count}` : ''}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
          <div className="mt-4 space-y-4">
            {productionServiceBuckets.active.length ? (
              productionServiceBuckets.active.map((service) => (
                <ServiceCard key={`${service.service_key}-${service.environment}`} service={service} historyHours={historyHours} />
              ))
            ) : productionServices.length ? (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50/80 px-4 py-4 dark:border-emerald-500/30 dark:bg-[#102017]">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                  <div>
                    <p className="text-sm font-semibold text-slate-900 dark:text-emerald-50">Nothing in production needs action right now</p>
                    <p className="mt-1 text-sm leading-6 text-slate-600 dark:text-emerald-100/85">
                      Keep production visible here, but leave the full service board collapsed unless you want to inspect details.
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowProductionDetails((value) => !value)}
                    aria-expanded={productionDetailsOpen}
                    className="border-slate-200 bg-white text-slate-900 hover:bg-slate-50 dark:border-emerald-400/20 dark:bg-black/20 dark:text-emerald-50 dark:hover:bg-black/30"
                  >
                    {productionDetailsOpen ? (
                      <ChevronDown className="mr-2 h-4 w-4" />
                    ) : (
                      <ChevronRight className="mr-2 h-4 w-4" />
                    )}
                    {productionDetailsOpen ? 'Hide production details' : 'Show production details'}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="rounded-xl border border-dashed border-slate-200 px-4 py-8 text-center dark:border-white/10">
                <p className="text-sm font-semibold text-slate-900 dark:text-white">No production monitoring snapshots yet</p>
                <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-gray-300">
                  Once the watcher reports in, production services will show up here.
                </p>
              </div>
            )}
            {productionDetailsOpen && productionServiceBuckets.healthy.length ? (
              <div className="space-y-4">
                {productionServiceBuckets.healthy.map((service) => (
                  <ServiceCard key={`${service.service_key}-${service.environment}`} service={service} historyHours={historyHours} />
                ))}
              </div>
            ) : null}
          </div>
        </Card>

        <Card ref={localSectionRef} className={`border-slate-200 bg-white/90 p-5 dark:border-white/10 dark:bg-white/[0.03] ${highlightedSectionClass(['local', 'local-checks'])}`}>
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex items-center gap-2">
              <Server className="h-5 w-5 text-slate-700 dark:text-slate-300" />
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-lg font-semibold text-slate-900 dark:text-white">Local machine checks</p>
                  <Badge className="border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300">
                    This machine only
                  </Badge>
                </div>
                <p className="text-sm text-slate-600 dark:text-gray-300">
                  {localSummary.downCount > 0 || localSummary.degradedCount > 0
                    ? `${localSummary.downCount} down, ${localSummary.degradedCount} degraded, ${localSummary.upCount} healthy across ${localSummary.sourceCount} reporting sources.`
                    : localSummary.isStale
                      ? `${localSummary.upCount} last-known healthy services, but local watcher data is stale.`
                      : `${localSummary.upCount} healthy services across ${localSummary.sourceCount} reporting sources.`}
                </p>
                {localSummary.isStale ? (
                  <p className="mt-1 text-xs leading-5 text-amber-700 dark:text-amber-300">
                    {formatStaleDetail(localSummary, 'local watcher')}
                  </p>
                ) : null}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowLocalSection((value) => !value)}
                aria-expanded={localSectionOpen}
                className="border-slate-200 bg-white text-slate-900 hover:bg-slate-50 dark:border-white/10 dark:bg-black/20 dark:text-white dark:hover:bg-black/30"
              >
                {localSectionOpen ? (
                  <ChevronDown className="mr-2 h-4 w-4" />
                ) : (
                  <ChevronRight className="mr-2 h-4 w-4" />
                )}
                {localSectionOpen ? 'Hide local machine checks' : 'Show local machine checks'}
              </Button>
              <Button
                type="button"
                onClick={onRunLocalChecks}
                disabled={localChecksDisabled}
                className="bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60"
              >
                <Server className={`mr-2 h-4 w-4 ${runningLocalChecks ? 'animate-pulse' : ''}`} />
                {runningLocalChecks ? 'Running checks...' : isLocalAdminHost ? 'Run local checks' : 'Open locally to run checks'}
              </Button>
            </div>
          </div>
          <p className="mt-3 text-xs leading-5 text-slate-500 dark:text-gray-400">
            {localChecksHelperText}
          </p>
          <div className="mt-4 rounded-xl border border-slate-200 bg-white/80 px-4 py-4 dark:border-white/10 dark:bg-white/[0.02]">
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-gray-500">
              Local quick actions
            </p>
            <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-gray-300">
              These are the three local services behind this laptop view. Use the commands here when you need to recover local runtime without hunting through docs.
            </p>
            <div className="mt-4 space-y-3">
              {LOCAL_SERVICE_GUIDE.map((item) => (
                <LocalQuickActionRow
                  key={item.serviceKey}
                  item={item}
                  onCopyStartCommand={copyLocalServiceStartCommand}
                />
              ))}
            </div>
          </div>
          {localSectionOpen ? (
            <div className="mt-4 space-y-4">
              {localSources.length ? (
                <div className="rounded-xl border border-slate-200 bg-slate-50/80 px-4 py-3 dark:border-white/10 dark:bg-black/10">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-gray-500">
                    Reporting sources
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {localSourceSummary.map((source) => (
                      <div
                        key={source.key}
                        className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-700 dark:border-white/10 dark:bg-black/20 dark:text-gray-200"
                      >
                        <span className="font-semibold">{source.machineLabel}</span>
                        <span className="ml-2 text-slate-500 dark:text-gray-400">
                          {source.typeLabel}
                          {source.count > 1 ? ` x${source.count}` : ''}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
              {localServices.length ? localServices.map((service) => (
                <ServiceCard key={`${service.service_key}-${service.environment}`} service={service} historyHours={historyHours} />
              )) : (
                <div className="rounded-xl border border-dashed border-slate-200 px-4 py-8 text-center dark:border-white/10">
                  <p className="text-sm font-semibold text-slate-900 dark:text-white">No local monitoring snapshots yet</p>
                  <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-gray-300">
                    Local watcher reports from the Mac mini or laptop will appear here once configured.
                  </p>
                </div>
              )}
            </div>
          ) : localServices.length && localSummary.downCount === 0 && localSummary.degradedCount === 0 ? (
            localSummary.isStale ? (
              <div className={`mt-4 rounded-xl border px-4 py-4 ${STALE_WARNING_CARD_CLASS}`}>
                <p className="text-sm font-semibold text-amber-950 dark:text-amber-50">Local watcher data is stale</p>
                <p className="mt-1 text-sm leading-6 text-amber-900 dark:text-amber-100/90">
                  {formatStaleDetail(localSummary, 'local watcher')}
                </p>
              </div>
            ) : (
              <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50/80 px-4 py-4 dark:border-emerald-500/30 dark:bg-[#102017]">
                <p className="text-sm font-semibold text-slate-900 dark:text-emerald-50">Local checks look healthy right now</p>
                <p className="mt-1 text-sm leading-6 text-slate-600 dark:text-emerald-100/85">
                  Leave this collapsed unless you want to inspect machine-specific detail.
                </p>
              </div>
            )
          ) : null}
        </Card>

        <Card ref={operationsGuideRef} className={`border-slate-200 bg-white/90 p-5 dark:border-white/10 dark:bg-white/[0.03] ${highlightedSectionClass('operating-guide')}`}>
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex items-center gap-2">
              <Wrench className="h-5 w-5 text-slate-700 dark:text-slate-300" />
              <div>
                <p className="text-lg font-semibold text-slate-900 dark:text-white">Operating guide</p>
                <p className="text-sm text-slate-600 dark:text-gray-300">Keep this collapsed until you need process detail.</p>
              </div>
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowOperationsGuide((value) => !value)}
              aria-expanded={showOperationsGuide}
              className="border-slate-200 bg-white text-slate-900 hover:bg-slate-50 dark:border-white/10 dark:bg-black/20 dark:text-white dark:hover:bg-black/30"
            >
              {showOperationsGuide ? (
                <ChevronDown className="mr-2 h-4 w-4" />
              ) : (
                <ChevronRight className="mr-2 h-4 w-4" />
              )}
              {showOperationsGuide ? 'Hide guide' : 'Show guide'}
            </Button>
          </div>
          {showOperationsGuide ? (
            <div className="mt-4 space-y-4">
              <div className="rounded-xl border border-slate-200 bg-white/80 px-4 py-3 dark:border-white/10 dark:bg-black/10">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-gray-500">Primary alert</p>
                <p className="mt-2 text-sm leading-6 text-slate-700 dark:text-gray-300">
                  Mac mini or laptop watcher posts the first Slack alert on state change and stays the fastest signal.
                </p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white/80 px-4 py-3 dark:border-white/10 dark:bg-black/10">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-gray-500">Backup confirmation</p>
                <p className="mt-2 text-sm leading-6 text-slate-700 dark:text-gray-300">
                  UptimeRobot free remains production-only backup monitoring. Treat it as a slower 5-minute outside-in confirmation, not the primary source of truth.
                </p>
              </div>
              <RecoveryPolicyCard
                eyebrow="Production"
                title="Shared production recovery stays manual"
                badgeLabel="Manual only"
                tone={productionSummary.downCount > 0 ? 'down' : 'up'}
                detail="Production incidents should stay read-only here for now. Confirm the failure, use the watcher source and incident trail for clarity, and recover through the real platform owner flow instead of one-click restarts."
                note="This keeps Version 2 safe while the monitoring model hardens."
              />
              <RecoveryPolicyCard
                eyebrow="Local"
                title="One safe local recovery assist"
                badgeLabel={isLocalAdminHost ? 'Assisted manual' : 'Localhost only'}
                tone={localSummary.downCount > 0 ? 'degraded' : 'unknown'}
                detail="If the local backend is down on this machine, the safest first assist is to copy the known backend start command and run it yourself in Terminal. This avoids pretending the admin app can safely restart arbitrary local processes for you."
                note={isLocalAdminHost
                  ? 'Use this only when the backend is not already running on this machine.'
                  : 'Open the admin app locally on the machine you want to recover before using this assist.'}
                actionLabel="Copy local backend start command"
                onAction={copyLocalBackendStartCommand}
                actionDisabled={!isLocalAdminHost}
              />
            </div>
          ) : null}
        </Card>

        <Card ref={incidentHistoryRef} className={`border-slate-200 bg-white/90 p-5 dark:border-white/10 dark:bg-white/[0.03] ${highlightedSectionClass('incident-history')}`}>
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex items-center gap-2">
              <Clock3 className="h-5 w-5 text-slate-700 dark:text-slate-300" />
              <div>
                <p className="text-lg font-semibold text-slate-900 dark:text-white">Incident history</p>
                <p className="text-sm text-slate-600 dark:text-gray-300">Past state changes for context, collapsed by default so history does not compete with current action.</p>
              </div>
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowIncidentHistory((value) => !value)}
              aria-expanded={showIncidentHistory}
              className="border-slate-200 bg-white text-slate-900 hover:bg-slate-50 dark:border-white/10 dark:bg-black/20 dark:text-white dark:hover:bg-black/30"
            >
              {showIncidentHistory ? (
                <ChevronDown className="mr-2 h-4 w-4" />
              ) : (
                <ChevronRight className="mr-2 h-4 w-4" />
              )}
              {showIncidentHistory ? 'Hide incident history' : `View history (${filteredIncidents.length})`}
            </Button>
          </div>
          {showIncidentHistory ? (
            <div className="mt-4 space-y-4">
              <div className="flex flex-wrap gap-2">
                {INCIDENT_FILTERS.map((option) => (
                  <Button
                    key={option}
                    type="button"
                    variant={incidentFilter === option ? 'default' : 'outline'}
                    onClick={() => setIncidentFilter(option)}
                    className={incidentFilter === option
                      ? 'bg-slate-900 text-white hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100'
                      : 'border-slate-200 bg-white text-slate-900 hover:bg-slate-50 dark:border-white/10 dark:bg-black/20 dark:text-white dark:hover:bg-black/30'}
                  >
                    {option === 'all' ? 'All incidents' : option === 'production' ? 'Production only' : 'Local only'}
                  </Button>
                ))}
              </div>
              <div className="space-y-3">
                {filteredIncidents.length ? filteredIncidents.map((incident) => (
                  <IncidentRow
                    key={incident.incident_id}
                    incident={incident}
                    highlighted={Boolean(targetRecordId) && targetRecordId === incident.incident_id}
                  />
                )) : (
                  <div className="rounded-xl border border-dashed border-slate-200 px-4 py-8 text-center dark:border-white/10">
                    <p className="text-sm font-semibold text-slate-900 dark:text-white">No incidents in this filter yet</p>
                    <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-gray-300">
                      Once a matching service changes state, that incident trail will show up here.
                    </p>
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </Card>
      </div>
    </div>
  );
}
